import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, Card, CardContent, Grid2, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert, Accordion, AccordionSummary, AccordionDetails, Divider } from '@mui/material';
import { useGetMilestones, useSkillBoxAddress } from '../../assets/nodeApi/getApi/getCommonApi';
import { postActivityLmsOrder, postVerifyActivityLmsPurchase, postAddress } from '../../assets/nodeApi/postApi/postCommonApi';
import { putAddress } from '../../assets/nodeApi/putApi/putSchoolApi';
import { deleteAddress } from '../../assets/nodeApi/deleteApi/deleteAPi';
import useAuthStore from '../../shared/store/useAuthStore';
import { useToaster } from '../../shared/utils/ToasterProvider';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ImagePath, ImageRelativePath, Page_Padding, Constants, Main_URL, Skillvation_Dashboard_URL, ssoLoginUrl } from '../../shared/constants';
import Loader from '../../shared/utils/Loader';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import useResponsive from '../../shared/utils/Responsive';
import { ImgSkeleton, TextSkeleton } from '../../shared/utils/SkeletonCom';
import TabComponentsForCourse from '../../shared/components/Tabs/TabComponentsForCourse';
import TabPanel from '../../shared/components/Tabs/TabPanel';
import { useReviews } from '../../assets/api/useGetApi';
import ReviewRatingCom from '../../shared/components/ReviewRatingCom';
import CustomDivider from '../../shared/utils/CustomDivider';
import useActivityLMSPurchaseStore from '../../shared/store/useActivityLMSPurchaseStore';
import useVisitedPathStore from '../../shared/store/useVisitedPathStore';
import AddressFormDialog from '../../shared/components/AddressFormDialog';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import ActivityLMSCoupon from './ActivityLMSCoupon';
import useCouponStore from '../../shared/store/useCouponStore';
import { ImageListItem } from '@mui/material';
import { indexedDBStorage } from '../../shared/utils/indexedDB/storage';

// Helper to get image URL
const getImageUrl = (imageName) => {
    if (!imageName) return 'https://via.placeholder.com/800x600';
    if (imageName.startsWith('http')) return imageName;
    if (imageName.startsWith('/uploads')) {
        const baseUrl = Main_URL ? Main_URL.replace(/\/$/, '') : 'https://myskool.club';
        return `${baseUrl}${imageName}`;
    }
    return `${ImagePath}${ImageRelativePath}${imageName}`;
};

const PriceRow = ({ label, value, isTotal = false, color = '#111' }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: '4px' }}>
        <Typography variant="body2" color={isTotal ? color : "grey.600"} fontWeight={isTotal ? 600 : 400}>
            {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2" color={color} fontWeight={isTotal ? 700 : 500}>
                ₹ {value}
            </Typography>
        </Box>
    </Box>
);

export const ImageSection = ({ itemData, isLoading }) => {

    return (
        <Grid2 container spacing={"1rem"} px={Page_Padding} mb={4}>
            {/* LEFT IMAGE */}

            <Grid2
                size={{ xs: 8, md: 8 }}
                sx={{
                    height: { xs: 160, sm: 280, md: 460 },
                    borderRadius: { xs: "0.6rem", md: "1.2rem" },
                    backgroundImage: isLoading
                        ? "none"
                        : `url(${itemData?.[0]?.img})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    display: "flex",
                    alignItems: "stretch",
                    height: "auto", // Remove fixed height
                    borderRadius: "1.5rem",
                    overflow: "hidden",
                    position: "relative",
                }}
            >
                <ImageListItem
                    sx={{
                        width: "100%",
                        height: "100%",
                        aspectRatio: "16/9",
                        maxHeight: 460,
                        "@media (max-width: 600px)": {
                            aspectRatio: "4/3",
                            maxHeight: 350,
                        },
                        "& .MuiImageListItem-img": {
                            objectFit: "cover",
                            width: "100%",
                            height: "100%",
                        },
                    }}
                >
                    {isLoading ? (
                        <ImgSkeleton />
                    ) : (
                        <img
                            src={itemData?.[0]?.img}
                            alt="Featured"
                            loading="lazy"
                            style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                            }}
                        />
                    )}
                </ImageListItem>
            </Grid2>

            {/* RIGHT IMAGES */}
            <Grid2
                size={{ xs: 4, md: 4 }}
                container
                direction="column"
                spacing={"1rem"}
                sx={{ height: { xs: 160, sm: 280, md: 460 }, display: 'flex' }}
            >
                <Grid2
                    size={12}
                    sx={{
                        flex: 1,
                        borderRadius: { xs: "0.6rem", md: "1.2rem" },
                        backgroundImage: `url(${Constants.customImages.CarouselImg2})`,
                        backgroundSize: "100% 100%",
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                    }}
                />
                <Grid2
                    size={12}
                    sx={{
                        flex: 1,
                        borderRadius: { xs: "0.6rem", md: "1.2rem" },
                        backgroundImage: `url(${Constants.customImages.CarouselImg3})`,
                        backgroundSize: "100% 100%",
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                    }}
                />
            </Grid2>
        </Grid2>
    );
};


function ActivityLMSCourse() {
    const { id } = useParams();
    const location = useLocation();
    const navState = location.state || {};

    // Seed from navigation state first; anything missing gets backfilled
    // asynchronously from IndexedDB below (indexedDBStorage.get returns a Promise,
    // so it can't be read synchronously during render like sessionStorage could).
    const [courseFallback, setCourseFallback] = useState({
        courseImage: navState.courseImage,
        coursePrice: navState.coursePrice,
        courseName: navState.courseName,
        tax: navState.tax,
        tax_type: navState.tax_type,
        shipping_charges: navState.shipping_charges,
    });

    const {
        courseImage,
        coursePrice,
        courseName,
        tax,
        tax_type,
        shipping_charges,
    } = courseFallback;

    // Backfill any missing fields from IndexedDB once, on mount / id change.
    useEffect(() => {
        const isComplete =
            courseFallback.courseImage &&
            courseFallback.coursePrice != null &&
            courseFallback.courseName;

        if (isComplete) return;

        let isCancelled = false;

        const loadCourseFromIndexedDB = async () => {
            try {
                const data = await indexedDBStorage.get("activityLmsData");

                if (isCancelled || !data) return;

                const courseDetails = Array.isArray(data) ? data : (data?.courses || []);
                const course = courseDetails.find((c) => String(c.id) === String(id));

                if (course) {
                    setCourseFallback((prev) => ({
                        courseImage: prev.courseImage || course.cover_image,
                        coursePrice: prev.coursePrice != null ? prev.coursePrice : course.price,
                        courseName: prev.courseName || course.title || course.course_name || course.name,
                        tax: prev.tax != null ? prev.tax : course.tax,
                        tax_type: prev.tax_type != null ? prev.tax_type : course.tax_type,
                        shipping_charges: prev.shipping_charges != null ? prev.shipping_charges : course.shipping_charges,
                    }));
                }
            } catch (error) {
                console.error("Failed to load course data from IndexedDB:", error);
            }
        };

        loadCourseFromIndexedDB();

        return () => {
            isCancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const navigate = useNavigate();

    const user = useAuthStore((state) => state.user);
    const token = useAuthStore((state) => state.accessToken);


    const { showToaster, handleClose } = useToaster();

    const setPurchaseComplete = useActivityLMSPurchaseStore((state) => state.setPurchaseComplete);
    const isPurchased = useActivityLMSPurchaseStore((state) => state.purchasedCourses[id]);
    const couponData = useCouponStore((state) => state.couponData);

    const [qty, setQty] = useState(1);
    const [expandedAccordion, setExpandedAccordion] = useState(null);
    const [taxValue, setTaxValue] = useState(tax ? Number(tax) : 0);
    const [taxType, setTaxType] = useState(tax_type ? Number(tax_type) : 1);
    const [shippingCharge, setShippingCharge] = useState(shipping_charges ? Number(shipping_charges) : 0);

    // Once tax / tax_type / shipping_charges arrive later (e.g. backfilled from
    // IndexedDB after the initial render), sync them into local state.
    useEffect(() => {
        if (tax != null) setTaxValue(Number(tax));
        if (tax_type != null) setTaxType(Number(tax_type));
        if (shipping_charges != null) setShippingCharge(Number(shipping_charges));
    }, [tax, tax_type, shipping_charges]);


    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpandedAccordion(isExpanded ? panel : null);
        if (isExpanded) setAddressListOpen(false);
    };

    const handleIncrement = () => setQty(prev => prev + 1);
    const handleDecrement = () => setQty(prev => (prev > 1 ? prev - 1 : 1));
    const handleManualChange = (e) => {
        const val = e.target.value.replace(/[^0-9]/g, '');
        if (val !== '') {
            setQty(parseInt(val, 10));
        } else {
            setQty('');
        }
    };
    const handleBlur = () => {
        if (qty === '' || qty < 1) setQty(1);
    };

    const createOrderMutation = useMutation({
        mutationFn: (payload) => postActivityLmsOrder(payload),
    });

    // Fetch milestones using id
    const { data: milestonesData, isLoading: isMilestonesLoading, isError } = useGetMilestones(id);
    const milestones = milestonesData?.data || [];

    // Fetch reviews
    const { data: reviewsData, isLoading: isReviewsLoading } = useReviews(id, { page: 1, limit: 10 });

    const [activeTab, setActiveTab] = useState(0);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const selectedMilestone = milestones[activeTab];

    const queryClient = useQueryClient();
    const [addressDialogOpen, setAddressDialogOpen] = useState(false);
    const [editingAddress, setEditingAddress] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [addressToDelete, setAddressToDelete] = useState(null);
    const [addressListOpen, setAddressListOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, msg: '', type: 'success' });

    const { data: addressData } = useSkillBoxAddress(user?.role);
    const addresses = addressData?.data || [];
    const [selectedAddressId, setSelectedAddressId] = useState(null);
    const deliveryAddress = addresses.find(a => a.id === selectedAddressId) || addresses[0];

    React.useEffect(() => {
        if (addresses && addresses.length > 0) {
            const isValid = addresses.some(a => a.id === selectedAddressId);
            if (!isValid) {
                setSelectedAddressId(addresses[0].id);
            }
        } else if (addresses && addresses.length === 0 && selectedAddressId !== null) {
            setSelectedAddressId(null);
        }
    }, [addresses, selectedAddressId]);

    React.useEffect(() => {
        if (token) {
            queryClient.invalidateQueries({
                queryKey: ["v1/address"],
            });
        }
    }, [token, queryClient]);

    const handleSaveAddress = async (values) => {
        const payload = {
            full_name: values.full_name,
            phone: values.contactNumber,
            address_line_1: values.address,
            address_line_2: "",
            city: values.city,
            state: values.state,
            country: values.country,
            postal_code: values.pincode,
            address_type: values.address_type,
        };
        try {
            if (editingAddress) {
                await putAddress(editingAddress.id, payload);
                setSnackbar({ open: true, msg: 'Address updated successfully!', type: 'success' });
            } else {
                await postAddress(payload);
                setSnackbar({ open: true, msg: 'Address added successfully!', type: 'success' });
            }
            queryClient.invalidateQueries({ queryKey: ["v1/address"] });
            setAddressDialogOpen(false);
        } catch (err) {
            setSnackbar({ open: true, msg: 'Error saving address', type: 'error' });
        }
    };

    const handleDeleteAddressClick = (id) => {
        setAddressToDelete(id);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDeleteAddress = async () => {
        if (addressToDelete) {
            try {
                await deleteAddress(addressToDelete);
                queryClient.invalidateQueries({ queryKey: ["v1/address"] });
                setSnackbar({ open: true, msg: 'Address deleted successfully!', type: 'success' });
                if (selectedAddressId === addressToDelete) {
                    setSelectedAddressId(null);
                }
            } catch (err) {
                setSnackbar({ open: true, msg: 'Error deleting address', type: 'error' });
            }
        }
        setDeleteDialogOpen(false);
        setAddressToDelete(null);
    };

    const LoginButton = () => {
        useVisitedPathStore.getState().setLastVisitedPath(location.pathname);
        localStorage.setItem("lastVisitedPath", location.pathname);
        window.location.href = ssoLoginUrl;
        return
    }

    const handlePurchase = () => {

        if (!token) {
            useVisitedPathStore.getState().setLastVisitedPath(location.pathname);
            localStorage.setItem("lastVisitedPath", location.pathname);
            window.location.href = ssoLoginUrl;
            return
        }

        if (addresses.length === 0) {
            showToaster('Warning!', 'Please add a shipping address to proceed', 'warning');
            return;
        }

        const payload = {
            price: coursePrice,
            quantity: qty,
            event_id: id,
            address_id: selectedAddressId,
            discount_id: couponData?.discountId ? Number(couponData.discountId) : null,
            details: {
                firstname: user?.firstname || '',
                lastname: user?.lastname || '',
                email: user?.email || ''
            }
        };

        createOrderMutation.mutate(payload, {
            onSuccess: (response) => {
                const orderData = response?.data || response;
                const orderId = orderData?.payment_order_id || orderData?.order?.id || orderData?.order?.payment_order_id || response?.payment_order_id;

                if (!orderId) {
                    showToaster('Warning!', 'Failed to get order ID from server', 'warning');
                    return;
                }

                const options = {
                    key: response?.key || orderData?.key || response?.data?.key,
                    amount: orderData?.order?.amount || orderData?.amount,
                    currency: orderData?.order?.currency || orderData?.currency || "INR",
                    name: "Myskill.club",
                    description: `Payment for ${selectedMilestone?.name || 'Milestone'}`,
                    order_id: orderId,
                    handler: async (paymentResponse) => {
                        const paymentData = {
                            razorpay_payment_id: paymentResponse.razorpay_payment_id,
                            razorpay_order_id: paymentResponse.razorpay_order_id,
                            razorpay_signature: paymentResponse.razorpay_signature,
                            purchaseId: response?.purchaseId || orderData?.purchaseId || response?.data?.purchaseId || orderId,
                        };
                        try {
                            const verifyResponse = await postVerifyActivityLmsPurchase(paymentData);
                            if (verifyResponse?.data?.success || verifyResponse?.success || verifyResponse?.data?.message) {
                                showToaster('Success', 'Payment successful', 'success', {
                                    showLink: true,
                                    linkText: "Click to view purchased course",
                                    onClickLink: () => window.location.href = `${Skillvation_Dashboard_URL}courses`,
                                    autoClose: false
                                });
                                setPurchaseComplete(id);
                            } else {
                                showToaster('Error', verifyResponse?.data?.message || verifyResponse?.message || 'Payment verification failed', 'error');
                            }
                        } catch (error) {
                            showToaster('Error', error?.response?.data?.message || error?.message || 'Payment error', 'error');
                        }
                    },
                    theme: { color: "#DC143C" }
                };
                const rzp = new window.Razorpay(options);
                rzp.on('payment.failed', function (response) {
                    showToaster('Error', response.error.description || 'Payment failed', 'error');
                });
                rzp.open();
            },
            onError: (error) => {
                showToaster('Error', error?.response?.data?.error?.description || error?.response?.data?.message || error?.message || "Payment initiation failed", 'error');
            }
        });
    };

    const isLoading = isMilestonesLoading;

    if (isError) return <Typography>Error loading details.</Typography>;

    // Prepare images for top section
    const topImages = [];
    if (courseImage) {
        topImages.push(courseImage);
    } else if (selectedMilestone?.milestoneImages) {
        topImages.push(selectedMilestone.milestoneImages);
    }

    const itemData = topImages.map((img, idx) => ({ img: getImageUrl(img), title: `Image ${idx + 1}` }));
    while (itemData.length < 3) {
        itemData.push({ img: 'https://via.placeholder.com/800x600', title: 'Placeholder' });
    }

    const renderCourseContent = () => {
        if (isLoading) return <Loader />;
        if (!selectedMilestone && milestones.length === 0) {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 8,
                        textAlign: 'center',
                        mt: 2
                    }}
                >
                    <FlagOutlinedIcon sx={{ fontSize: 50, color: '#bbb8b8ff', mb: 2 }} />
                    <Typography variant="body1" fontWeight={600} color="#1A1A1A" mb={1}>
                        No Milestones Available
                    </Typography>
                    <Typography variant="body3" color="grey.500">
                        There are currently no milestones found for this course.
                    </Typography>
                </Box>
            );
        }

        return (
            <Box>
                {/* Milestone Details */}
                <Typography variant='h6' mb={1.5} fontWeight={600} color="#1A1A1A">
                    {selectedMilestone?.name || "Milestone Name"}
                </Typography>
                <Typography variant='body1' color='grey.600' mb={3} sx={{ lineHeight: 1.6 }}>
                    {selectedMilestone?.description || "Milestone description in details..."}
                </Typography>

                {/* Sessions as Cards */}
                {selectedMilestone?.course_sprint?.flatMap(sprint => sprint.course_sessions || [])?.map((session, sessionIdx) => (
                    <Box
                        key={session?.id || sessionIdx}
                        sx={{
                            bgcolor: '#F9F9F9',
                            borderRadius: '7px',
                            p: 2,
                            mb: 1.5,
                        }}
                    >
                        <Typography variant="body1" fontWeight={600} color="#1A1A1A">
                            {session?.sessionname}
                        </Typography>
                    </Box>
                ))}
            </Box>
        );
    };

    const tabData = milestones.map((m, idx) => ({
        label: m.name || `Milestone ${idx + 1}`,
        component: idx === activeTab ? renderCourseContent() : null
    }));

    const subtotal = (coursePrice) * qty;
    const totalTax = taxType === 1 ? (subtotal * taxValue / 100) : (taxValue * qty);
    const totalShippingCharge = shippingCharge * qty;

    const initialTotal = taxType === 1 ? subtotal + totalShippingCharge : subtotal + totalShippingCharge + totalTax;
    const appliedCoupon = couponData?.isPassed ? { discount: couponData.discountAmount, code: couponData.couponCode } : null;
    const total = Math.max(0, initialTotal - (appliedCoupon ? appliedCoupon.discount : 0));

    return (
        <Box className="body course_detail" sx={{ pb: 10, pt: 4 }}>
            {/* Top Image Section */}
            <ImageSection itemData={itemData} isLoading={isLoading} />

            <Box px={Page_Padding}>
                <Grid2 container spacing={6}>
                    {/* Left Section: Tabs and Main Content */}
                    <Grid2 size={{ xs: 12, md: 7, lg: 8 }}>
                        {courseName && (
                            <Typography variant="h5" fontWeight={600} mb={2} color="#1A1A1A">
                                {courseName}
                            </Typography>
                        )}
                        {milestones.length > 0 ? (
                            <>
                                <TabComponentsForCourse
                                    isCheckBox={false}
                                    tabs={tabData}
                                    value={activeTab}
                                    onChange={handleTabChange}
                                    isPadding={false}
                                />
                                {tabData.map((tab, index) => (
                                    <TabPanel key={index} value={activeTab} index={index}>
                                        {tab?.component}
                                    </TabPanel>
                                ))}
                            </>
                        ) : (
                            renderCourseContent()
                        )}
                    </Grid2>

                    {/* Right Section: Purchase Sidebar */}
                    <Grid2 size={{ xs: 12, md: 5, lg: 4 }}>
                        <Card
                            sx={{
                                position: { xs: 'relative', md: 'sticky' },
                                top: { xs: 0, md: '100px' },
                                borderRadius: '24px',
                                boxShadow: '0px 12px 40px rgba(0,0,0,0.06)',
                                border: '1px solid #F0F0F0',
                                p: "4px",
                                mt: milestones.length > 0 ? { xs: 4, md: "70px" } : { xs: 4, md: 0 },
                                mb: { xs: 4, md: 0 }
                            }}
                        >
                            <CardContent sx={{ p: 3 }}>
                                {/* Price Header */}
                                <Accordion
                                    expanded={expandedAccordion === 'paymentDetails'}
                                    onChange={handleAccordionChange('paymentDetails')}
                                    sx={{ boxShadow: 'none', '&:before': { display: 'none' }, bgcolor: 'transparent', m: '0 !important' }}
                                    disableGutters
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        sx={{
                                            p: 0,
                                            minHeight: 'auto',
                                            '& .MuiAccordionSummary-content': { m: 0 }
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                            <Typography variant="h4" sx={{ color: '#000', fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                                                <CurrencyRupeeIcon sx={{ color: 'red.main' }} /> {(total).toLocaleString('en-IN')}
                                            </Typography>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ p: 0, mt: 2, pb: 3 }}>
                                        <Typography variant="body2" fontWeight={500} mb={1.5} color="#111">
                                            Payment Details
                                        </Typography>

                                        <PriceRow label="Subtotal" value={subtotal.toFixed(2)} />
                                        <PriceRow label={taxType === 1 ? "Tax (Inclusive)" : taxType === 2 ? "Tax (Exclusive)" : "Tax"} value={totalTax.toFixed(2)} />
                                        <PriceRow label="Shipping Charge" value={totalShippingCharge.toFixed(2)} />
                                        {/* <PriceRow
                                                label={`Total`}
                                                value={initialTotal.toFixed(2)}
                                            /> */}

                                        {appliedCoupon && (
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: '4px' }}>
                                                <Typography variant="body2" color="red.main" fontWeight={300}>
                                                    Coupon applied
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Typography variant="body2" color="red.main">- ₹ {appliedCoupon.discount}</Typography>
                                                </Box>
                                            </Box>
                                        )}

                                        <Divider sx={{ my: '0.85rem' }} />

                                        <PriceRow label="Total" value={total.toFixed(2)} isTotal color="red.main" />
                                    </AccordionDetails>
                                </Accordion>

                                <Box sx={{ borderBottom: '1px solid #F0F0F0', mb: 4 }} />

                                {/* Milestones List in Sidebar */}
                                <Accordion
                                    expanded={expandedAccordion === 'milestones'}
                                    onChange={handleAccordionChange('milestones')}
                                    sx={{ boxShadow: 'none', '&:before': { display: 'none' }, }}
                                    disableGutters
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        sx={{
                                            backgroundColor: "#ebeaea93",
                                            borderRadius: "12px",
                                            px: 2,
                                            mb: 3,
                                            minHeight: '48px',
                                            '& .MuiAccordionSummary-content': { my: 0 }
                                        }}
                                    >
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>Milestones</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ px: 1, pt: 2, pb: 2, mt: -3 }}>
                                        {milestones.length > 0 ? (
                                            <Box>
                                                {milestones.map((m, idx) => (
                                                    <Box key={m.id || idx} sx={{ mb: 3 }}>
                                                        <Typography variant="body2" fontWeight={600} color="#333" mb={0.5}>
                                                            {m.name || `Mile stone ${idx + 1}`}
                                                        </Typography>
                                                        <Typography variant="body3" color="grey.500" sx={{
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 1,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden',
                                                            lineHeight: 1.5
                                                        }}>
                                                            {m.description || "Milestone description in details..."}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        ) : (
                                            <Box>
                                                <Typography variant="body3" color="grey.500">No milestones available.</Typography>
                                            </Box>
                                        )}
                                    </AccordionDetails>
                                </Accordion>

                                {/* Address Feature */}
                                {user && (
                                    <Box sx={{ mb: 4 }}>
                                        {deliveryAddress ? (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        cursor: 'pointer',
                                                        backgroundColor: "#ebeaea93",
                                                        borderRadius: "12px",
                                                        p: 1
                                                    }}
                                                    onClick={() => {
                                                        setAddressListOpen(!addressListOpen);
                                                        if (!addressListOpen) setExpandedAccordion(false);
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                        <PlaceOutlinedIcon sx={{ color: 'red.main', fontSize: '2rem' }} />
                                                        <Box>
                                                            <Typography variant="body2" sx={{ color: 'red.main', fontWeight: 500 }}>
                                                                {deliveryAddress.full_name} ({deliveryAddress.address_type})
                                                            </Typography>
                                                            <Typography variant="body3" sx={{ color: "grey.500", numberOfLines: 1 }}>
                                                                {deliveryAddress.address_line_1}{deliveryAddress.address_line_2 ? ', ' + deliveryAddress.address_line_2 : ''} ...
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                    {addressListOpen ? <KeyboardArrowUpIcon sx={{ color: 'red.main', fontSize: '1.5rem' }} /> : <KeyboardArrowDownIcon sx={{ color: 'red.main', fontSize: '1.5rem' }} />}
                                                </Box>

                                                {/* Expanded Address List */}
                                                {addressListOpen && (
                                                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                        {addresses.map((addr) => (
                                                            <Box
                                                                key={addr.id}
                                                                sx={{
                                                                    p: 1.5,
                                                                    border: '1px solid',
                                                                    borderColor: selectedAddressId === addr.id ? 'red.main' : '#E7E7E7',
                                                                    borderRadius: '8px',
                                                                    cursor: 'pointer',
                                                                    bgcolor: selectedAddressId === addr.id ? '#FFF0F0' : 'transparent',
                                                                    transition: 'all 0.2s',
                                                                }}
                                                                onClick={() => {
                                                                    setSelectedAddressId(addr.id);
                                                                    setAddressListOpen(false);
                                                                }}
                                                            >
                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                                    <Box sx={{ flex: 1 }}>
                                                                        <Typography variant="body2" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                            {addr.full_name} ({addr.address_type})
                                                                        </Typography>
                                                                        <Typography variant="body3" color="grey.600" sx={{ mt: 0.5, lineHeight: 1.5 }}>
                                                                            {addr.address_line_1}{addr.address_line_2 ? ', ' + addr.address_line_2 : ''},<br />
                                                                            {addr.city}, {addr.state} {addr.postal_code}<br />
                                                                            {addr.country}
                                                                        </Typography>
                                                                    </Box>
                                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={(e) => { e.stopPropagation(); setEditingAddress(addr); setAddressDialogOpen(true); }}
                                                                            sx={{ color: '#888', '&:hover': { color: 'red.main' } }}
                                                                        >
                                                                            <EditIcon sx={{ fontSize: '18px' }} />
                                                                        </IconButton>
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={(e) => { e.stopPropagation(); handleDeleteAddressClick(addr.id); }}
                                                                            sx={{ color: '#888', '&:hover': { color: 'red.main' } }}
                                                                        >
                                                                            <CloseIcon sx={{ fontSize: '18px' }} />
                                                                        </IconButton>
                                                                    </Box>
                                                                </Box>
                                                                <Typography variant="body3" color="grey.600">
                                                                    Phone: {addr.phone}
                                                                </Typography>
                                                            </Box>
                                                        ))}
                                                        <Button
                                                            variant="outlined"
                                                            fullWidth
                                                            onClick={() => { setEditingAddress(null); setAddressDialogOpen(true); }}
                                                            sx={{ mt: 1, borderColor: 'red.main', color: 'red.main', '&:hover': { bgcolor: '#FFF0F0' }, textTransform: 'none', fontWeight: 600 }}
                                                        >
                                                            Add New Address
                                                        </Button>
                                                    </Box>
                                                )}
                                            </Box>
                                        ) : (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                <Typography variant="body3" color="grey.600">
                                                    Add shipping address to further continue the process
                                                </Typography>
                                                <Button
                                                    fullWidth
                                                    variant="text"
                                                    onClick={() => setAddressDialogOpen(true)}
                                                    sx={{
                                                        bgcolor: '#FFF0F0',
                                                        color: 'red.main',
                                                        borderRadius: '10px',
                                                        py: '11px',
                                                        textTransform: 'none',
                                                        fontWeight: 600,
                                                        '&:hover': { bgcolor: '#FFE0E0' }
                                                    }}
                                                >
                                                    Add Shipping Address
                                                </Button>
                                            </Box>
                                        )}
                                    </Box>
                                )}

                                {/* Quantity Selector */}
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    border: '1px solid rgba(227, 30, 36, 0.2)',
                                    borderRadius: '8px',
                                    bgcolor: 'rgba(227, 30, 36, 0.04)',
                                    p: '2px',
                                    mb: 3
                                }}>
                                    <Button onClick={handleDecrement} sx={{ minWidth: '40px', p: 0.5, color: 'red.main', fontSize: '1.2rem', fontWeight: 400, backgroundColor: '#fff' }}>-</Button>
                                    <input
                                        type="text"
                                        value={qty}
                                        onChange={handleManualChange}
                                        onBlur={handleBlur}
                                        style={{
                                            textAlign: 'center',
                                            border: 'none',
                                            // background: 'transparent'
                                            backgroundColor: '#fff',
                                            color: 'red.main',
                                            fontWeight: 600,
                                            fontSize: '1rem',
                                            width: '150px',
                                            height: '35px',
                                            outline: 'none',
                                            fontFamily: 'inherit'
                                        }}
                                    />
                                    <Button onClick={handleIncrement} sx={{ minWidth: '40px', p: 0.5, backgroundColor: '#fff', color: 'red.main', fontSize: '1.2rem', fontWeight: 400 }}>+</Button>
                                </Box>



                                {/* Purchase Button */}
                                <Button
                                    variant="contained"
                                    fullWidth
                                    size="large"
                                    onClick={handlePurchase}
                                    disabled={createOrderMutation.isPending || isPurchased}
                                    sx={{
                                        py: 1,
                                        borderRadius: '8px',
                                        bgcolor: isPurchased ? 'grey.400' : 'red.main',
                                        color: '#fff',
                                        fontSize: '1.05rem',
                                        fontWeight: 600,
                                        mb: 2,
                                        textTransform: 'none',
                                        boxShadow: 'none',
                                        '&:hover': {
                                            bgcolor: isPurchased ? 'grey.400' : 'red.main',
                                            boxShadow: 'none',
                                        }
                                    }}
                                >
                                    {isPurchased ? 'Purchase Complete' : 'Purchase Now'}
                                </Button>
                                {user ? (
                                    <ActivityLMSCoupon courseId={id} totalPrice={initialTotal} />
                                ) : (
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mb: '12px',
                                        gap: '12px'
                                    }}>
                                        <Typography variant='body3' sx={{
                                            color: 'green.main',
                                            fontWeight: 500,
                                            lineHeight: 1,
                                        }}>
                                            Do you have an account ??<Button sx={{ fontSize: "12px", fontWeight: 500, lineHeight: "16px", color: 'red.main' }} onClick={() => LoginButton()}>Login</Button>
                                        </Typography>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid2>
                </Grid2>
            </Box>

            {/* REVIEW SECTION */}
            <Box px={Page_Padding} mt={1} mb={2}>
                <CustomDivider orientation='horizontal' />
                <Box textAlign="center" my={5} display="flex" flexDirection="column" alignItems="center">
                    <Box display="flex" alignItems="center" justifyContent="center" gap={{ xs: 2, md: 4 }}>
                        <Box component="img" src={Constants.customImages.GrainLeft} alt="Grain Left" sx={{ width: { xs: "50px", sm: "60px", md: "80px" } }} />
                        <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                            <Typography variant="h1" sx={{ color: '#16192c', fontWeight: 500, lineHeight: 1 }}>
                                {reviewsData?.overallReview?.averageRating || '5.0'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" mt={1} sx={{ maxWidth: '300px', mx: 'auto' }}>
                                This course is students favourite based on their review and ratings
                            </Typography>
                        </Box>
                        <Box component="img" src={Constants.customImages.GrainRight} alt="Grain Right" sx={{ width: { xs: "50px", sm: "60px", md: "80px" } }} />
                    </Box>
                </Box>

                <Typography variant="h5" mb={3} mt={7} fontWeight={500}>Review & Rating</Typography>

                {isReviewsLoading ? (
                    <TextSkeleton line={3} />
                ) : (
                    <>
                        {reviewsData?.reviews?.length > 0 ? (
                            reviewsData.reviews.map((review, index) => (
                                <ReviewRatingCom key={review.id || index} review={review} />
                            ))
                        ) : (
                            <Typography variant="body2" color="grey.400">No reviews yet for this course.</Typography>
                        )}
                    </>
                )}
            </Box>
            {/* ── Address Dialog ── */}
            <AddressFormDialog
                open={addressDialogOpen}
                onClose={() => setAddressDialogOpen(false)}
                onSave={handleSaveAddress}
                initialValues={editingAddress ? {
                    full_name: editingAddress.full_name,
                    address: editingAddress.address_line_1,
                    pincode: editingAddress.postal_code,
                    city: editingAddress.city,
                    state: editingAddress.state,
                    address_type: editingAddress.address_type,
                    country: editingAddress.country,
                    contactNumber: editingAddress.phone,
                } : null}
            />

            {/* ── Delete Confirm Dialog ── */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} PaperProps={{ sx: { borderRadius: '12px' } }}>
                <DialogTitle sx={{ fontWeight: 600 }}>Delete Address</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        Are you sure you want to delete this address? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: '1rem', pt: 0 }}>
                    <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: 'grey.600', textTransform: 'none' }}>Cancel</Button>
                    <Button onClick={handleConfirmDeleteAddress} variant="contained" sx={{ bgcolor: 'red.main', color: '#fff', '&:hover': { bgcolor: '#b01030' }, textTransform: 'none' }}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Snackbar ── */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                    severity={snackbar.type}
                    variant="filled"
                    sx={{ borderRadius: '10px', fontWeight: 600 }}
                >
                    {snackbar.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default ActivityLMSCourse;